import { z } from "zod";
import { isoDateSchema } from "../utils/dates";
import { boatSchema } from "./boatSchema";

/**
 * Filters that narrow which boats a search shows. Add future filters
 * (e.g. hasMotor) here and in `matchesBoatFilters`.
 */
export const boatFiltersSchema = z.object({
  partySize: z
    .number({ error: "Party size must be a number." })
    .int("Party size must be a whole number.")
    .min(1, "Party size must be at least 1.")
    .max(100)
    .optional(),
});
export type BoatFilters = z.infer<typeof boatFiltersSchema>;

export const boatSearchSchema = z
  .object({
    filters: boatFiltersSchema,
    /** Optional trip dates; when given, each boat is checked for availability. */
    startDate: isoDateSchema.optional(),
    endDate: isoDateSchema.optional(),
  })
  .refine((q) => (q.startDate === undefined) === (q.endDate === undefined), {
    message: "Choose both a first and a last day.",
    path: ["endDate"],
  })
  .refine((q) => !q.startDate || !q.endDate || q.endDate >= q.startDate, {
    message: "The last day must be on or after the first day.",
    path: ["endDate"],
  });
export type BoatSearch = z.infer<typeof boatSearchSchema>;

export const boatSearchResultSchema = z.object({
  today: isoDateSchema,
  /** Problems with the requester rather than any boat (e.g. at their booking limit). */
  personProblems: z.array(z.string()),
  boats: z.array(
    z.object({
      boat: boatSchema,
      /** Undefined when no dates were searched. */
      available: z.boolean().optional(),
      problems: z.array(z.string()),
    })
  ),
  hiddenByFilters: z.number().int().nonnegative(),
});
export type BoatSearchResult = z.infer<typeof boatSearchResultSchema>;
